/**
 * Navigation of the dynamic property tree.
 */
import type Creative from '../Creative';
import { type IDynamicProperty } from './IDynamicProperty';
import type DynamicPropertiesManager from './Manager';
import type DynamicGroup from './Types/Group';

/**
 * The top of the tree `property` belongs to, found by following `parent`
 * links. A property with no parent is its own root; otherwise the root is
 * a group, since only groups have children.
 */
export function getRoot<P extends { parent: DynamicGroup | null | undefined }>(
    property: P
): P | DynamicGroup {
    let root: P | DynamicGroup = property;
    while (root.parent) root = root.parent;
    return root;
}

/**
 * The Creative that owns the tree `property` belongs to.
 *
 * Undefined for a tree that belongs to no document. A detached tree's root is
 * a plain DynamicGroup with no parentDocument - which is why this reads
 * through Partial rather than asserting the cast. ClipboardManager's `current`
 * is exactly such a group.
 *
 * Takes only the `parent` it needs rather than a whole IDynamicProperty.
 * Asset and Template pass `this` from getters that `label` is inferred from,
 * and checking them against the full interface would go round in a circle.
 */
export function getOwningCreative(property: {
    parent: DynamicGroup | null | undefined;
}): Creative | undefined {
    const root = getRoot(property) as Partial<DynamicPropertiesManager>;
    return root.parentCreative;
}

/**
 * The dot-separated names leading from just below the root of `property`'s
 * tree down to `property` itself - e.g. "dimensions.width". The root has no
 * name in the path, so a root's own path is ''.
 *
 * Names are joined as they are, so a name containing a dot makes the path
 * ambiguous. That is fine for display, which is all this is for.
 */
function getPropertyPath(property: IDynamicProperty<any>): string {
    const names: string[] = [];
    for (let p = property; p.parent; p = p.parent) names.push(p.name);
    return names.reverse().join('.');
}

/**
 * Constructs the error reported when a CSV value can't be applied to
 * `property`, naming the ad it belongs to and the property's path within it.
 *
 * The ad's label and ID come from the tree's root, which for an ad is its
 * DynamicPropertiesManager. A detached tree's root is a plain DynamicGroup
 * with no document, and a property with no parent is its own root - hence
 * the Partial, as in getOwningCreative.
 */
export function makeCSVImportError(
    property: IDynamicProperty<any>,
    message: string
): Error {
    const root = getRoot(property) as Partial<DynamicPropertiesManager>;

    const adLabel = root.getChild?.('label')?.value;
    const adLabelLog = adLabel ? JSON.stringify(adLabel) : '(no label)';

    const adId = root.parentDocument?.id;
    const adIdLog = adId || 'no id';

    const propertyPath = getPropertyPath(property);
    const ppLog = propertyPath ? JSON.stringify(propertyPath) : '(no path)';

    return new Error(
        `CSV Import Error: Ad ${adLabelLog} (${adIdLog}): Property ${ppLog}: ${message}`
    );
}
